import { loadStripe, Stripe } from '@stripe/stripe-js'

/**
 * Stripe Configuration Utility
 *
 * This module provides centralized access to Stripe configuration and initialization.
 * It handles loading the Stripe.js library and provides access to Stripe API keys
 * and Price IDs for subscription tiers.
 */

// Stripe publishable key from environment variables
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY

if (!stripePublishableKey) {
  console.warn(
    'REACT_APP_STRIPE_PUBLISHABLE_KEY is not set. Stripe functionality will not work. ' +
      'Please add it to your .env.local file.'
  )
}

// Singleton instance of Stripe
let stripePromise: Promise<Stripe | null> | null = null

/**
 * Get the Stripe instance
 * This function lazily loads and caches the Stripe.js library
 *
 * @returns Promise that resolves to the Stripe instance or null if key is missing
 */
export const getStripe = (): Promise<Stripe | null> => {
  if (!stripePromise && stripePublishableKey) {
    stripePromise = loadStripe(stripePublishableKey)
  }
  return stripePromise || Promise.resolve(null)
}

/**
 * Stripe Price IDs for subscription tiers
 * These are configured in the Stripe Dashboard under Products > Prices
 * Each tier has both monthly and annual price IDs
 */
export const stripePriceIds = {
  small: {
    monthly: process.env.REACT_APP_STRIPE_SMALL_MONTHLY_PRICE_ID || '',
    annual: process.env.REACT_APP_STRIPE_SMALL_ANNUAL_PRICE_ID || ''
  },
  medium: {
    monthly: process.env.REACT_APP_STRIPE_MEDIUM_MONTHLY_PRICE_ID || '',
    annual: process.env.REACT_APP_STRIPE_MEDIUM_ANNUAL_PRICE_ID || ''
  },
  large: {
    monthly: process.env.REACT_APP_STRIPE_LARGE_MONTHLY_PRICE_ID || '',
    annual: process.env.REACT_APP_STRIPE_LARGE_ANNUAL_PRICE_ID || ''
  }
}

/**
 * Check if Stripe is properly configured
 *
 * @returns true if all required Stripe configuration is present
 */
export const isStripeConfigured = (): boolean => {
  return !!(
    stripePublishableKey &&
    stripePriceIds.small.monthly &&
    stripePriceIds.small.annual &&
    stripePriceIds.medium.monthly &&
    stripePriceIds.medium.annual &&
    stripePriceIds.large.monthly &&
    stripePriceIds.large.annual
  )
}

/**
 * Get missing Stripe configuration items
 * Useful for debugging configuration issues
 *
 * @returns Array of missing configuration keys
 */
export const getMissingStripeConfig = (): string[] => {
  const missing: string[] = []

  if (!stripePublishableKey) {
    missing.push('REACT_APP_STRIPE_PUBLISHABLE_KEY')
  }
  if (!stripePriceIds.small.monthly) {
    missing.push('REACT_APP_STRIPE_SMALL_MONTHLY_PRICE_ID')
  }
  if (!stripePriceIds.small.annual) {
    missing.push('REACT_APP_STRIPE_SMALL_ANNUAL_PRICE_ID')
  }
  if (!stripePriceIds.medium.monthly) {
    missing.push('REACT_APP_STRIPE_MEDIUM_MONTHLY_PRICE_ID')
  }
  if (!stripePriceIds.medium.annual) {
    missing.push('REACT_APP_STRIPE_MEDIUM_ANNUAL_PRICE_ID')
  }
  if (!stripePriceIds.large.monthly) {
    missing.push('REACT_APP_STRIPE_LARGE_MONTHLY_PRICE_ID')
  }
  if (!stripePriceIds.large.annual) {
    missing.push('REACT_APP_STRIPE_LARGE_ANNUAL_PRICE_ID')
  }

  return missing
}

/**
 * Server-side Stripe configuration
 * These values should only be used in server-side code (Cloud Functions, webhooks)
 * NEVER expose these in client-side code
 */
export const serverStripeConfig = {
  secretKey: process.env.STRIPE_SECRET_KEY || '',
  webhookSecret: process.env.STRIPE_WEBHOOK_SECRET || ''
}
